// frontend/src/components/FeaturedProductsCarousel.js
import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import { useNavigate } from 'react-router-dom';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './FeaturedProductsCarousel.css';
import './Skeleton.css';
import SkeletonCard from './SkeletonCard';

const getLowestPriceVariant = (variants) => {
    if (!variants || variants.length === 0) {
        return { displayPrice: null, gram: null, originalPrice: null, offerPrice: null, isOnSale: false, discountPercentage: 0 };
    }
    let lowestPrice = Infinity;
    let lowestGram = '';
    let lowestOriginalPrice = Infinity;
    let lowestOfferPrice = null;
    let currentIsOnSale = false;
    variants.forEach(variant => {
        const effectivePrice = (variant.offerPrice > 0 && variant.offerPrice < variant.price) ? variant.offerPrice : variant.price;
        if (effectivePrice < lowestPrice) {
            lowestPrice = effectivePrice;
            lowestGram = variant.gram;
            lowestOriginalPrice = variant.price;
            lowestOfferPrice = variant.offerPrice;
            currentIsOnSale = (variant.offerPrice > 0 && variant.offerPrice < variant.price);
        }
    });
    let discountPercentage = 0;
    if (currentIsOnSale && lowestOriginalPrice > lowestOfferPrice) {
        discountPercentage = Math.round(((lowestOriginalPrice - lowestOfferPrice) / lowestOriginalPrice) * 100);
    }
    return { displayPrice: lowestPrice, gram: lowestGram, originalPrice: lowestOriginalPrice, offerPrice: lowestOfferPrice, isOnSale: currentIsOnSale, discountPercentage: discountPercentage };
};

const FeaturedProductsCarousel = ({ publicSpices = [] }) => {
    const [featuredSpices, setFeaturedSpices] = useState([]);
    const navigate = useNavigate();

    const handleViewAllProducts = () => {
        navigate('/products');
    };

    const swiperBreakpoints = {
        480: { slidesPerView: 2, spaceBetween: 15 },
        640: { slidesPerView: 2, spaceBetween: 20 },
        768: { slidesPerView: 3, spaceBetween: 30 },
        1024: { slidesPerView: 4, spaceBetween: 40 },
    };

    useEffect(() => {
        if (publicSpices.length > 0) {
            // FILTER: Show products where featuredOrder is set AND isComingSoon is FALSE (or undefined)
            const filtered = publicSpices.filter(s => s.featuredOrder !== null && s.isComingSoon !== true)
                                         .sort((a, b) => a.featuredOrder - b.featuredOrder);
            setFeaturedSpices(filtered);
        }
    }, [publicSpices]);

    const renderCarouselContent = () => {
        if (publicSpices.length === 0) {
            const numberOfSkeletonsToRender = 4;
            return (
                <Swiper modules={[Navigation, Pagination, A11y]} slidesPerView={2} spaceBetween={10} navigation pagination={{ clickable: true }} breakpoints={swiperBreakpoints}>
                    {[...Array(numberOfSkeletonsToRender)].map((_, index) => (
                        <SwiperSlide key={`skeleton-${index}`}><SkeletonCard /></SwiperSlide>
                    ))}
                </Swiper>
            );
        }

        if (featuredSpices.length === 0) {
            return <div className="featured-carousel-status">No featured products to display.</div>;
        }

        return (
            <Swiper modules={[Navigation, Pagination, A11y]} slidesPerView={2} spaceBetween={10} navigation pagination={{ clickable: true }} breakpoints={swiperBreakpoints}>
                {featuredSpices.map(spice => {
                    const { displayPrice, gram, originalPrice, offerPrice, isOnSale, discountPercentage } = getLowestPriceVariant(spice.variants);
                    const imageUrl = spice.imageUrls && spice.imageUrls.length > 0 ? spice.imageUrls[0] : spice.imageUrl;

                    // CHECK MASTER SALE TOGGLE
                    const showSaleBadge = spice.onSale;

                    return (
                        <SwiperSlide key={spice._id}>
                            <div className="featured-product-card" onClick={() => navigate(`/product/${spice._id}`)}>
                                
                                {/* --- NEW: SALE BADGE OVERLAY --- */}
                                {showSaleBadge && (
                                    <div className="carousel-sale-badge">SALE</div>
                                )}

                                <div className="inner-card-content">
                                    {/* --- UPDATED: Image is now wrapped in a div to contain the zoom effect --- */}
                                    <div className="featured-image-wrapper">
                                        <img src={imageUrl || 'https://placehold.co/300x200/cccccc/000000?text=No+Image'} alt={spice.name} className="featured-product-image" onError={(e)=>{e.target.onerror = null; e.target.src='https://placehold.co/300x200/cccccc/000000?text=No+Image'}} />
                                    </div>
                                    <div className="product-info">
                                        <h3>{spice.name} {gram && gram !== 'No Gram' && gram.toLowerCase().includes('g') && (<span className="product-gram-display"> ({gram})</span>)}</h3>
                                        <div className="product-card-info-stack">
                                            {isOnSale ? (
                                                <div className="carousel-product-prices">
                                                    {discountPercentage > 0 && (<span className="carousel-discount-badge"><span className="arrow">&#x2193;</span>{discountPercentage}% OFF</span>)}
                                                    <span className="carousel-original-price">£{originalPrice.toFixed(2)}</span>
                                                    <span className="carousel-offer-price">£{offerPrice.toFixed(2)}</span>
                                                </div>
                                            ) : (
                                                <p className="carousel-current-price-only">From: £{displayPrice !== null ? displayPrice.toFixed(2) : 'N/A'}</p>
                                            )}
                                            <div className="product-card-actions-carousel">
                                                <button className="buy-product-button" onClick={(e) => { e.stopPropagation(); navigate(`/product/${spice._id}`); }}>View Details</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        );
    };

    return (
        <div className="featured-products-background-wrapper">
            <section className="featured-products-section">
                <div className="section-header-row">
                    <h2 className="section-title">New Arrivals & Featured Spices</h2>
                    <button className="view-all-products-button" onClick={handleViewAllProducts}>View All Products</button>
                </div>
                {renderCarouselContent()}
            </section>
        </div>
    );
};

export default FeaturedProductsCarousel;